import React, { useEffect, useState } from "react";
import styled from "styled-components";

import CognitiveFunction from "../../components/CognitiveFunction";
import {
  cognitiveFunctions,
  cogFuncPriorities,
  domAux,
  intSens,
  thinkFeel,
} from "../../utils/globalConst";
import { cogFuncAllConfig } from "../../data/cogFuncAllConfig";
import { round2Dec, isOdd } from "../../utils/helpers";
import useWindowDimensions from "../../utils/hooks/useWindowDimensions";
import CognitiveBackground from "../../components/CognitiveBackground";
import PJLabels from "./PJLabels";

import HumanOutline from "../../svg/layout_dark_human.svg";

const Container = styled.div`
  padding: 0 10px;

  h1 {
    font-size: 1.5em;
    z-index: 100;

    span.label {
      font-size: 0.5em;
    }
  }

  @media (min-width: 500px) {
    h1 {
      font-size: 2em;

      span.label {
        font-size: 0.75em;
      }
    }

    h2 {
      font-size: 3em;
    }
  }
`;

const SVGWheel = styled.svg`
  position: absolute;
  top: 50%;
  bottom: 50%;
  transform: translate(-50%, -50%);
`;

const SVGBackground = styled.svg`
  position: absolute;
  top: 50%;
  bottom: 50%;
  transform: translate(-50%, -50%);
`;

const HumanImgWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  pointer-events: none;

  img {
    width: 160px;
    height: auto;
  }
`;

const SVGPJLabelsContainer = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;

  .pj-label-p {
    font-weight: ${({ pjLetter }) => (pjLetter === "P" ? `bold` : `500`)};
    font-size: ${({ pjLetter }) => (pjLetter === "P" ? `1.5em` : `1em`)};
  }

  .pj-label-j {
    font-weight: ${({ pjLetter }) => (pjLetter === "J" ? `700` : `bold`)};
    font-size: ${({ pjLetter }) => (pjLetter === "J" ? `1.5em` : `1em`)};
  }
`;

const CogFuncExplorer = ({
  type = "ESTJ",
  max = 0.99,
  min = 0.01,
  paramStates,
  mbtiObj,
}) => {
  const [{ ns }, { tf }, { xvn }] = paramStates;
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();

  const paramValues = {
    n: ns,
    s: round2Dec(max - ns),
    t: tf,
    f: round2Dec(max - tf),
  };

  /* Generate function stack */
  const cogFuncShortIdx = (cfshort) =>
    cognitiveFunctions.map((d) => d.short).indexOf(cfshort);
  const isDomExvert = xvn < max / 2;
  const funcsStack = Object.entries(paramValues)
    .sort((a, b) => a[1] - b[1])
    .map((d, i) => {
      const cfshort = d[0];
      const isExvert = isOdd(i) ? !isDomExvert : isDomExvert;
      return {
        cogFunc: cognitiveFunctions[cogFuncShortIdx(cfshort)].function,
        isExvert,
        stackFunc: cogFuncPriorities[i].function,
      };
    });

  const sfIdx = (sf) => funcsStack.map((d) => d.stackFunc).indexOf(sf);
  const cogFuncShort = (cf) =>
    cognitiveFunctions[cognitiveFunctions.map((d) => d.function).indexOf(cf)]
      .short;
  const getFuncVal = (sf = "dominant") => {
    const { cogFunc } = funcsStack[sfIdx(sf)];

    if (sf === "dominant") {
      return paramValues[cogFuncShort(cogFunc)];
    } else return paramValues[cogFuncShort(cogFunc)];
  };

  /* Setup custom config styles for the chart to use */
  const { cogFuncConfig, stackFuncConfig } = cogFuncAllConfig["default"];
  const sfConfigCustom = stackFuncConfig.map((sf) => {
    return {
      ...sf,
      style: {
        rotation: -18 * 5 * getFuncVal(sf.stackFunc),
      },
    };
  });
  const xvnConfigCustom = [
    {
      xVersFunc: "extraversion",
      style: {
        areaRatio: xvn / 1,
        innerRadiusRatio: isDomExvert ? xvn : max - xvn,
      },
    },
    {
      xVersFunc: "introversion",
      style: {
        areaRatio: 1 / xvn,
        innerRadiusRatio: isDomExvert ? max - xvn : xvn,
      },
    },
  ];

  /* Identify 4 letter MBTI code */
  const [{ pjLetter }, setMbti] = mbtiObj;
  const getDomCogFunc = (daArr, pjArr) =>
    funcsStack
      .filter((d) => daArr.some((e) => e === d.stackFunc))
      .filter((d) => pjArr.some((e) => e === d.cogFunc))[0].cogFunc;
  const isPOrJ = (setFunc, pr) =>
    setFunc.some((d) => d === funcsStack[sfIdx(pr)].cogFunc) ? "P" : "J";

  useEffect(() => {
    setMbti({
      ieLetter: isDomExvert ? "E" : "I",
      nsLetter: cogFuncShort(getDomCogFunc(domAux, intSens)).toUpperCase(),
      tfLetter: cogFuncShort(getDomCogFunc(domAux, thinkFeel)).toUpperCase(),
      pjLetter: isDomExvert
        ? isPOrJ(intSens, "dominant")
        : isPOrJ(intSens, "auxiliary"),
    });
  }, [ns, tf, xvn]);

  /* Chart dimensions */
  // const radius = windowWidth <= 500 ? windowWidth / 2 - 50 : 240;
  const getRadius = () => (windowWidth <= 500 ? windowWidth / 2 - 50 : 240);
  const [radius, setRadius] = useState(getRadius());
  const svgDims = { height: radius * 2, width: radius * 2, radius };
  const { width, height } = svgDims;

  useEffect(() => {
    setRadius(getRadius);
  }, [windowWidth, windowWidth]);

  /* Oragnize props */
  const config = {
    cogFuncConfig,
    stackFuncConfig: sfConfigCustom,
    xVersionConfig: xvnConfigCustom,
  };

  const svgBgProps = {
    width: windowWidth,
    height: windowHeight,
    viewBox: `0 0 ${windowWidth} ${windowHeight}`,
  };

  const cogFuncProps = {
    ...svgDims,
    chartId: "cogfunc-explorer",
    type,
    config,
    hasTransition: false,
    showLabels: true,
  };

  const circleProps = {
    fill: "#424242",
    r: 20,
    cx: 10,
    cy: 50,
  };

  const pjTextProps = {
    fill: "#ffffff",
    fontSize: "20px",
    textAnchor: "middle",
    dominantBaseline: "central",
    y: 50,
  };

  const pjContainerProps = {
    width: windowWidth,
    height: 100,
    viewBox: `0 0 ${windowWidth} 100`,
    radius,
    pjLetter,
  };

  /* Tooltip on Arc and ArcHollow hover */
  const onArcHover = ({ currCogFunc, currStackFunc, isHandleExvert }) => {
    console.log(currCogFunc + " | " + currStackFunc + " | " + isHandleExvert);
  };

  return (
    <Container className="cogfunc-explorer-container" {...{ radius }}>
      <SVGBackground {...svgBgProps}>
        {funcsStack.map((s, i) => (
          <CognitiveBackground
            key={`cogbg-${i}`}
            {...{ config, s }}
          ></CognitiveBackground>
        ))}
      </SVGBackground>
      <HumanImgWrapper className="human-img-wrapper">
        <img src={HumanOutline} alt="human outline icon" />
      </HumanImgWrapper>
      <SVGWheel viewBox={`0 0 500 500`} {...{ width, height }}>
        {funcsStack.map((s, i) => (
          <CognitiveFunction
            key={`cogfunc-${i}`}
            {...{ ...cogFuncProps, ...s }}
            onArcHover={(funcObjs) => onArcHover(funcObjs)}
          />
        ))}
      </SVGWheel>
      <SVGPJLabelsContainer className="pj-labels" {...pjContainerProps}>
        <PJLabels {...{ circleProps, windowWidth, radius, pjTextProps }} />
      </SVGPJLabelsContainer>
    </Container>
  );
};

export default CogFuncExplorer;
